import classNames from 'classnames';
import { FullPost } from 'model';
import React, { useEffect, useRef, useState } from 'react';
import { Swiper as SwiperCore } from 'swiper';
import { Swiper, SwiperSlide } from 'swiper/react';
import 'swiper/swiper.min.css';
import styles from './BigImageContainer.module.css';
import { DotIndicator } from './Forms/DotIndicator';
import { IconImageDelete } from './IconImageDelete';
import { ImageSkeleton } from './ImageSkeleton';
import { useBackendImageURLs } from '../hooks/useBackendDomain.js';

function calculatePadding(aspectRatioValues: number[]) {
	const max = Math.max(...aspectRatioValues);
	const scaledArr = aspectRatioValues.map(val => ((max - val) / max) * 100);
	const calculateTopSpaceForIndex = (index: number) => {
		const percentage = scaledArr[index] / 2;
		return max * (percentage / 100);
	};
	const calculatedTopSpaces = aspectRatioValues.map((_, index) => calculateTopSpaceForIndex(index));
	return calculatedTopSpaces;
}

const loadImg = (src: string) =>
	new Promise<HTMLImageElement | null>(resolve => {
		const img = new Image();
		img.src = src;
		img.onload = () => resolve(img);
		img.onerror = () => resolve(null);
	});

export const BigImageContainer: React.FC<{ post: FullPost; edit?: boolean; images?: string[] }> = ({
	post,
	edit,
	images
}) => {
	const [imageAspectRatios, setImageAspectRatios] = useState<number[]>([]);
	const [currentImageIndex, setCurrentImageIndex] = useState<number>(0);
	const [swiperInstance, setSwiperInstance] = useState<SwiperCore | null>(null);
	const [imageLoaded, setImageLoaded] = useState<boolean[]>([]);
	const maxHeightRatio = 140;

	const postImages = images ?? post.images;
	const postImagesBackendURLs = useBackendImageURLs(postImages.map(image => `/api/post/image/${post._id}/${image}`));
	const unfilteredPostImagesBackendURLs = useBackendImageURLs(
		post.images.map(image => `/api/post/image/${post._id}/${image}`)
	);
	const prevImageLengthRef = useRef(postImages.length);

	// Load all the images and get their aspect ratios
	useEffect(() => {
		const abort = { cancelled: false };
		async function loadImages() {
			if (post?.images.length > 0) {
				const imagePromises = unfilteredPostImagesBackendURLs.map(image => loadImg(image));
				const foundImages = await Promise.all(imagePromises);
				// Don't update if are cancelled
				if (!abort.cancelled) {
					const newRatios = foundImages
						.map(img => {
							const value = img
								? Math.min((img.naturalHeight / img.naturalWidth) * 100, maxHeightRatio)
								: null;
							return value ? (isNaN(value) ? null : value) : value;
						})
						.filter((val): val is number => val !== null);
					setImageAspectRatios(newRatios);
				}
			}
		}

		loadImages();
		return () => {
			abort.cancelled = true;
		};
		// This is a bit of a hack, as we want to detect when the urls themselseves change, not the array,
		// which gets redone every render. So we join the array into a string and use that as a dependency.
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [post, post._id, post.images, unfilteredPostImagesBackendURLs.join(',')]);

	const handleSlideChange = (swiper: SwiperCore) => {
		setCurrentImageIndex(swiper.activeIndex);
	};

	const handleClick = (index: number) => {
		swiperInstance?.slideTo(index);
	};

	const allImagesLoaded = imageLoaded.length > 0 && imageLoaded.every(val => val);

	useEffect(() => {
		if (allImagesLoaded && postImages.length > prevImageLengthRef.current) {
			const newIndex = postImages.length - 1;
			setCurrentImageIndex(newIndex);
			swiperInstance?.slideTo(newIndex, 100);
		}
		prevImageLengthRef.current = postImages.length;
	}, [postImages.length, allImagesLoaded, swiperInstance]);

	// Get the aspect ratios for the images that are currently being displayed,
	// Because imageAspectRatios contains all images, not just the ones that are being displayed
	const aspectRatioValues = post.images.reduce((acc: number[], image, index) => {
		if (postImages.includes(image)) {
			const val = imageAspectRatios[index] ?? 0;
			return [...acc, val];
		}
		return acc;
	}, []);

	const letterBoxPadding = calculatePadding(aspectRatioValues);

	return (
		<div className={styles.outerContainer}>
			<>
				{postImages.length > 1 && (
					<div className={styles.imageTracker}>
						{postImages.map((image, index) => (
							<div key={index} onClick={() => handleClick(index)} className={styles.imageTrackerDot}>
								<DotIndicator on={currentImageIndex === index} />
							</div>
						))}
					</div>
				)}
				{postImages.length > 0 && !allImagesLoaded && (
					<div className={styles.imageContainer}>
						<ImageSkeleton />
					</div>
				)}
				{postImages.length > 0 && (
					<Swiper
						spaceBetween={10}
						slidesPerView={1}
						onSlideChange={handleSlideChange}
						className={classNames(styles.imageContainer, {
							[styles.visible]: postImages.length !== 0
						})}
						// style={{ opacity: allImagesLoaded ? 1 : 0 }}
						onSwiper={setSwiperInstance}
					>
						{edit && (
							<div className={styles.imageUpload}>
								<IconImageDelete
									onDelete={() =>
										// eslint-disable-next-line no-sequences
										setImageLoaded(current => (current.splice(currentImageIndex, 1), current))
									}
									post={post}
									imageName={postImages[currentImageIndex]}
								/>
							</div>
						)}
						{postImagesBackendURLs.slice(0, 6).map((image, index) => (
							<SwiperSlide key={index}>
								<div
									className={`${styles.postImage}`}
									style={{
										paddingBottom: aspectRatioValues[index] ? `${aspectRatioValues[index]}%` : '0'
									}}
								>
									<img
										alt={`${post.title} - Slide ${index + 1}`}
										onLoad={() => {
											setImageLoaded(prev => {
												prev[index] = true;
												return [...prev];
											});
										}}
										onError={() => {
											setImageLoaded(prev => {
												prev[index] = true;
												return [...prev];
											});
										}}
										style={{
											paddingTop: letterBoxPadding[index] + '%'
										}}
										src={`${image}`}
									/>
								</div>
							</SwiperSlide>
						))}
					</Swiper>
				)}
			</>
		</div>
	);
};
